// SystemsView.jsx — THE SYSTEMS (Braeden 8/13)
//
//   "the systems needs to have everything the books ones has in it"
//
// This IS the old Books page, moved where it belongs. Books was showing the
// machinery — every automation Aria runs, grouped by how often it runs — which
// is a description of the SYSTEM, not a record. So the machinery lives here and
// Books became the ledger (BooksView.jsx).
//
// The status is NOT decided here. It comes from GET /api/automations/status,
// which asks each registered operator service its own health(). A page that
// claims work it isn't doing is worse than one that admits the gap.
import React from 'react';
import { getApiBase } from './apiBase';

const CAP_GROUPS = [
  ['Every day', [
    ['missed_calls', 'Missed calls', 'Texts back in seconds so the job is not lost'],
    ['calendar_invites', 'Calendar invites', 'Book jobs & send reminders'],
    ['text_reminders', 'Text reminders', 'Send & receive customer SMS'],
    ['new_leads', 'New leads', 'Every lead into one list, whatever the source'],
    ['days_route', "Today's route", 'Puts the day in drive order, least backtrack'],
    ['job_photos', 'Job photos', 'Before & after saved to the job — your proof'],
  ]],
  ['Every job', [
    ['quotes', 'Quotes & estimates', 'Priced from the scope, you edit before it goes'],
    ['invoices', 'Invoices', 'Create, send & chase invoices'],
    ['payments', 'Payments', 'Pay link — card, bank, or financing'],
    ['crew_hours', 'Crew hours', 'Clock in/out per job — feeds payroll & costing'],
  ]],
  ['Every week', [
    ['quote_chase', 'Quotes with no reply', 'Nudges the ones that went quiet'],
    ['overdue_invoices', 'Overdue invoices', 'Reminders that escalate until you are paid'],
    ['expenses', 'Receipts & spend', 'Logs every dollar out, categorised'],
    ['supplies', 'Supplies & materials', 'Reorder before you run out — fuel, dump, bags'],
    ['lead_sources', 'Where jobs came from', 'What each source cost vs the work it won'],
  ]],
  ['Every month', [
    ['reviews', 'Reviews', 'Asks happy customers, replies to every one'],
    ['email_promotions', 'Email promotions', 'Send promos & replies'],
    ['payroll', 'Payroll & contractors', 'Hours to pay, and who is a 1099'],
    ['repeat_customers', 'Repeat customers', 'Service agreements billed on schedule'],
    ['vehicle_upkeep', 'Truck & equipment', 'Service due by mileage or hours'],
    ['gbp_posts', 'Google Business page', 'Keeps the map listing active with real posts'],
  ]],
  ['Every quarter', [
    ['taxes', 'Quarterly taxes', 'Sets aside as you earn, files on time'],
    ['price_check', 'Price & margin check', 'Flags jobs where your cost outran the price'],
    ['vendor_papers', 'Sub & vendor paperwork', 'W-9s and insurance certs before they work'],
  ]],
  ['Every year', [
    ['renewals', 'Renewals & filings', 'License, insurance, vehicle reg, state report'],
    ['year_end', 'Year-end books', '1099s, W-2s, and the package your CPA wants'],
    ['winback', 'Win back old customers', 'Reaches out to anyone quiet 12 months'],
  ]],
];

// Own prefix (.sysv), not .books — the ledger owns .books now and two pages
// sharing a class prefix is how the day-theme white-on-white bug happened.
const STYLE = `
.sysv { --sv-ink:#eaf0f8; --sv-mut:rgba(255,255,255,0.55); --sv-card:rgba(255,255,255,0.045);
  --sv-line:rgba(255,255,255,0.10); --sv-acc:#0a84ff; --sv-on:#30d158; --sv-onbg:rgba(48,209,88,0.12);
  --sv-onedge:rgba(48,209,88,0.40); --sv-pill:rgba(255,255,255,0.07);
  padding:26px 20px 64px; }
/* DAY THEME — every colour redefined, not inherited, so nothing stays white by
   accident (the bug that made the old page invisible in day). */
.sysv.is-light { --sv-ink:#0e1420; --sv-mut:rgba(0,0,0,0.58); --sv-card:#fff;
  --sv-line:rgba(0,0,0,0.12); --sv-acc:#0a6bd4; --sv-on:#1a7f37; --sv-onbg:rgba(48,209,88,0.12);
  --sv-onedge:rgba(26,127,55,0.42); --sv-pill:rgba(0,0,0,0.06); }
.sysv__wrap { max-width:860px; margin:0 auto; }
.sysv__title { font-size:34px; font-weight:800; letter-spacing:-0.8px; color:var(--sv-ink); margin:0 0 4px; }
.sysv__sub { font-size:13px; font-weight:700; letter-spacing:0.1em; text-transform:uppercase; color:var(--sv-acc); margin-bottom:10px; }
.sysv__lead { font-size:15px; color:var(--sv-mut); margin:0 0 26px; max-width:60ch; }
.sysv__group { margin-top:26px; }
.sysv__ghead { display:flex; align-items:baseline; justify-content:space-between; gap:12px;
  margin:0 2px 10px; padding-bottom:8px; border-bottom:1px solid var(--sv-line); }
.sysv__when { font-size:12px; font-weight:800; letter-spacing:0.14em; text-transform:uppercase; color:var(--sv-acc); }
.sysv__count { font-size:11px; font-weight:700; color:var(--sv-mut); }
.sysv__row { display:grid; grid-template-columns:auto minmax(140px,0.85fr) 1.4fr auto; align-items:center;
  gap:14px; padding:15px 18px; margin-bottom:9px; border-radius:14px;
  background:var(--sv-card); border:1px solid var(--sv-line); }
.sysv__row.is-on { background:var(--sv-onbg); border-color:var(--sv-onedge); }
.sysv__dot { width:8px; height:8px; border-radius:50%; background:var(--sv-mut); opacity:0.5; }
.sysv__row.is-on .sysv__dot { background:var(--sv-on); opacity:1; box-shadow:0 0 10px var(--sv-onedge); }
.sysv__name { font-size:15px; font-weight:700; letter-spacing:-0.2px; color:var(--sv-ink); }
.sysv__what { font-size:13px; color:var(--sv-mut); }
.sysv__state { justify-self:end; white-space:nowrap; font-size:10px; font-weight:800; letter-spacing:0.08em;
  text-transform:uppercase; padding:5px 11px; border-radius:999px; background:var(--sv-pill); color:var(--sv-mut); }
.sysv__row.is-on .sysv__state { background:var(--sv-onbg); color:var(--sv-on); }
.sysv__jump { display:inline-flex; align-items:center; gap:7px; margin-top:18px; padding:10px 16px;
  border:1px solid var(--sv-line); background:var(--sv-card); color:var(--sv-ink); border-radius:999px;
  font-size:13px; font-weight:700; cursor:pointer; }
.sysv__jump:hover { border-color:var(--sv-acc); color:var(--sv-acc); }
@media (max-width:760px) {
  .sysv { padding:20px 14px 56px; }
  .sysv__title { font-size:27px; }
  .sysv__row { grid-template-columns:auto 1fr; gap:5px 12px; padding:15px 16px; }
  .sysv__what, .sysv__state { grid-column:2; }
  .sysv__state { justify-self:start; margin-top:5px; }
}
`;

export default function SystemsView({ theme, onNavigate }) {
  const [autos, setAutos] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${getApiBase()}/api/automations/status`);
        const j = await r.json();
        if (alive && j && j.ok) setAutos(j);
      } catch (_) { /* unknown beats a wrong claim */ }
    })();
    return () => { alive = false; };
  }, []);

  const st = (k) => (autos && autos.automations && autos.automations[k]) || null;

  return (
    <div className={`sysv${theme === 'day' ? ' is-light' : ''}`}>
      <style>{STYLE}</style>
      <div className="sysv__wrap">
        <h1 className="sysv__title">Systems</h1>
        <div className="sysv__sub">
          {autos ? `${autos.readyCount} of ${autos.total} running` : 'Checking what is running…'}
        </div>
        <p className="sysv__lead">Every system Aria runs for the business — what it does and whether it is on. What each one actually did is in the Books.</p>

        {CAP_GROUPS.map(([groupName, rows]) => {
          const on = rows.filter(([k]) => st(k) && st(k).ready).length;
          return (
            <div className="sysv__group" key={groupName}>
              <div className="sysv__ghead">
                <span className="sysv__when">{groupName}</span>
                <span className="sysv__count">{autos ? `${on}/${rows.length} on` : `${rows.length}`}</span>
              </div>
              {rows.map(([key, label, detail]) => {
                const s = st(key);
                const isOn = !!(s && s.ready);
                return (
                  <div className={`sysv__row${isOn ? ' is-on' : ''}`} key={key}>
                    <span className="sysv__dot" aria-hidden />
                    <span className="sysv__name">{label}</span>
                    <span className="sysv__what">{detail}</span>
                    <span className="sysv__state">{s ? (isOn ? 'Running' : s.needs) : '…'}</span>
                  </div>
                );
              })}
            </div>
          );
        })}

        {onNavigate && (
          <button type="button" className="sysv__jump" onClick={() => onNavigate('books')}>
            Open the Books — what these systems actually did →
          </button>
        )}
      </div>
    </div>
  );
}
